import { useBreakpoint } from 'components/BreakpointProvider';
import { useRouter } from 'next/router';
import { useOnSuccessActionModal } from 'components/inbox/hooks/useOnSuccessActionModal';
import useModal from 'components/remodal/v2/useModal';
import PreApproveModal from 'components/modals/PreApproveModal';
import { BookingActionButtonProps } from 'components/ui/BookingActionsBox/BookingActionButton';
import BookingActionsBox from 'components/ui/BookingActionsBox/BookingActionsBox';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import { preApproveAction, useEditInquiryModal } from '../bookingPanel/SidebarOwnerActions';
import usePreApproveCardShow from '../hooks/usePreApproveCardShow';
import { SpecialMessageProps } from './types';

export default function EditInquiryBoxActions({ userType, booking, index, conversation }: SpecialMessageProps) {
  const router = useRouter();
  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;
  const onSuccess = useOnSuccessActionModal();
  const rudderTrack = useRudderStackTrack();

  const [preApproveCardShow, hidePreApproveBox] = usePreApproveCardShow();

  const { modal: preApproveModal, openModal: openPreApproveModal } = useModal(
    <PreApproveModal booking={booking} onSuccess={onSuccess} />,
    {
      size: 'medium',
      isOnRight: true,
      title: smallScreen && 'Pre-approve inquiry',
      trackingName: 'pre_approve_modal',
    }
  );
  const { modal: editInquiryModal, openModal: openEditInquiryModal, isAvailable } = useEditInquiryModal(booking);

  const preApproveActionObj = preApproveAction(booking, router, openPreApproveModal);
  const inquiryAvailable = preApproveActionObj.available && isAvailable;

  const fireTracking = (action: 'pre_approve' | 'openEditBookingModal') => {
    rudderTrack('booking_action_owner', {
      bookingId: booking.id,
      bookingState: booking.state,
      action,
      location: 'conversation_action_box',
    });
  };

  const handlePreApprove = () => {
    preApproveActionObj.onClick();
    fireTracking('pre_approve');
  };

  const ownerHasResponded = !conversation?.info_messages?.some((msg) => msg.message_type === 'owner_has_not_responded');
  const title = ownerHasResponded
    ? `${booking.renter.first_name} is interested in your boat. Invite them to book by pre-approving.`
    : `Reply and invite ${booking.renter.first_name} to book by pre-approving.`;
  const subHeading = ownerHasResponded ? null : 'Guests are more likely to book if you respond to their message.';

  return (
    <>
      {inquiryAvailable &&
        preApproveCardShow &&
        index === 0 &&
        booking.state_read_by_user === 'inquiry' &&
        booking.package.available &&
        (userType === 'owner' || userType === 'boat manager') && (
          <BookingActionsBox
            title={title}
            ctaText="Pre-approve"
            subHeading={subHeading}
            subtitle="Pre-approved bookings will not block off dates in your calendar. This message is only visible to you."
            userType="owner"
            onSubmit={handlePreApprove}
            ctas={
              [
                isAvailable && {
                  ctaText: 'Edit inquiry',
                  userType: 'owner_outline',
                  onSubmit: () => {
                    fireTracking('openEditBookingModal');
                    openEditInquiryModal();
                  },
                },
              ].filter(Boolean) as BookingActionButtonProps[]
            }
            showCloseButton
            onClose={hidePreApproveBox}
          />
        )}

      {editInquiryModal}
      {preApproveModal}
    </>
  );
}
